// WebGPU bring-up: adapter/device negotiation, canvas configuration, and
// resize handling that respects devicePixelRatio without letting a 4K display
// on a laptop GPU tank the frame time.

export interface GpuContextOptions {
  /** Upper bound on rendered pixels; above this we scale DPR down. Default 1440p-ish. */
  maxPixels?: number;
  /** Hard cap on device pixel ratio. Default 2. */
  maxDpr?: number;
  /**
   * How much to render *beyond* the display's own pixels, before the caps.
   *
   * 1 renders one buffer pixel per device pixel. Above 1 is supersampling: the
   * frame is drawn larger and the browser scales it down, which is the only
   * lever this renderer has left against jagged card edges. The silhouette is
   * a signed-distance test resolved through `alphaToCoverageEnabled`, so a
   * rounded corner has no geometry for MSAA to smooth -- its coverage is
   * quantised into the four samples, five levels, and no amount of MSAA
   * tuning changes that. Drawing more pixels does.
   *
   * Below 1 is the opposite trade, and it is the one a phone wants.
   */
  renderScale?: number;
}

export class GpuContext {
  readonly adapter: GPUAdapter;
  readonly device: GPUDevice;
  readonly canvas: HTMLCanvasElement;
  readonly context: GPUCanvasContext;
  /** Preferred swapchain format, typically bgra8unorm. */
  readonly format: GPUTextureFormat;
  width = 1;
  height = 1;
  dpr = 1;

  private readonly maxPixels: number;
  private readonly maxDpr: number;
  private readonly renderScale: number;
  private readonly listeners = new Set<(w: number, h: number) => void>();
  private observer: ResizeObserver | null = null;

  private constructor(
    adapter: GPUAdapter, device: GPUDevice, canvas: HTMLCanvasElement,
    context: GPUCanvasContext, format: GPUTextureFormat,
    opts: GpuContextOptions,
  ) {
    this.adapter = adapter;
    this.device = device;
    this.canvas = canvas;
    this.context = context;
    this.format = format;
    this.maxPixels = opts.maxPixels ?? 2560 * 1440;
    this.maxDpr = opts.maxDpr ?? 2;
    this.renderScale = opts.renderScale ?? 1;
  }

  static async create(canvas: HTMLCanvasElement, opts: GpuContextOptions = {}): Promise<GpuContext> {
    if (!navigator.gpu) {
      throw new UnsupportedError(
        'This browser has no WebGPU. Chrome or Edge 113+ on Windows/macOS, or Chrome 121+ on Android.',
      );
    }

    const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
    if (!adapter) {
      throw new UnsupportedError(
        'No suitable GPU adapter. If you are on a laptop, check that hardware acceleration is enabled.',
      );
    }

    // Only ask for what we can actually fall back from.
    const wanted: GPUFeatureName[] = [];
    for (const f of ['float32-filterable', 'timestamp-query'] as GPUFeatureName[]) {
      if (adapter.features.has(f)) wanted.push(f);
    }

    const device = await adapter.requestDevice({
      requiredFeatures: wanted,
      requiredLimits: {
        maxTextureDimension2D: Math.min(8192, adapter.limits.maxTextureDimension2D),
        maxBufferSize: Math.min(268435456, adapter.limits.maxBufferSize),
      },
    });

    device.lost.then((info) => {
      // Not thrown: a lost device surfaces asynchronously and the app decides.
      console.error(`[gpu] device lost (${info.reason}): ${info.message}`);
    });

    // Validation and out-of-memory errors are otherwise silent; without this a
    // malformed pipeline just produces a black frame and no explanation.
    device.addEventListener('uncapturederror', (event) => {
      console.error('[gpu] uncaptured error:', (event as GPUUncapturedErrorEvent).error.message);
    });

    const context = canvas.getContext('webgpu');
    if (!context) throw new UnsupportedError('Could not acquire a WebGPU canvas context.');

    const format = navigator.gpu.getPreferredCanvasFormat();

    // The swapchain is asked for the ordinary display range, and that is not a
    // downgrade -- it is the range this renderer actually produces.
    //
    // It used to probe for `toneMapping: { mode: 'extended' }` and record the
    // answer in `hdr`, which nothing ever read. The probe is not free: where it
    // succeeds it changes how the *whole canvas* is composited, and it succeeds
    // on exactly one class of display -- the HDR panels phones have and most
    // desktop monitors do not. So the one genuinely platform-dependent thing in
    // the render path was a mode we opted into and never used.
    //
    // Never used is the load-bearing part. `agx` clamps the scene into the
    // display range and `linearToSrgb` leaves it there, so nothing this
    // renderer presents is above SDR white; extended range has nothing to
    // carry. The scene *is* unbounded -- a foil highlight reaches 30x white --
    // but the tone curve is where that gets spent, and it is spent before the
    // swapchain sees it. This is the hook to come back to if the foil rework
    // ever wants a highlight that is genuinely brighter than paper, and it
    // needs a tone mapper that emits above 1.0 to be worth anything.
    context.configure({
      device, format,
      alphaMode: 'opaque',
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });

    const ctx = new GpuContext(adapter, device, canvas, context, format, opts);
    ctx.attachResize();
    return ctx;
  }

  onResize(fn: (w: number, h: number) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  get aspect(): number {
    return this.width / this.height;
  }

  private attachResize(): void {
    const apply = (cssW: number, cssH: number) => {
      // The scale is applied to the display's ratio and *then* capped, so a
      // budget or a cap always wins: supersampling is what is asked for, not
      // what is promised, and a 4K panel must not be handed a 6K buffer.
      const raw = Math.min((window.devicePixelRatio || 1) * this.renderScale, this.maxDpr);
      // Scale back if the resulting buffer would exceed the pixel budget.
      const budget = Math.sqrt(this.maxPixels / Math.max(1, cssW * cssH * raw * raw));
      const dpr = raw * Math.min(1, budget);

      const w = Math.max(1, Math.round(cssW * dpr));
      const h = Math.max(1, Math.round(cssH * dpr));
      if (w === this.width && h === this.height) return;

      this.width = this.canvas.width = w;
      this.height = this.canvas.height = h;
      this.dpr = dpr;
      for (const fn of this.listeners) fn(w, h);
    };

    this.observer = new ResizeObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      // devicePixelContentBoxSize is exact when available; contentRect is CSS px.
      const box = e.devicePixelContentBoxSize?.[0];
      if (box) {
        const scale = window.devicePixelRatio || 1;
        apply(box.inlineSize / scale, box.blockSize / scale);
      } else {
        apply(e.contentRect.width, e.contentRect.height);
      }
    });

    try {
      this.observer.observe(this.canvas, { box: 'device-pixel-content-box' });
    } catch {
      this.observer.observe(this.canvas);
    }
    apply(this.canvas.clientWidth || 1, this.canvas.clientHeight || 1);
  }

  destroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.listeners.clear();
    this.device.destroy();
  }
}

/** Thrown when the machine or browser simply cannot run the renderer. */
export class UnsupportedError extends Error {
  override name = 'UnsupportedError';
}
